import { useQuery } from '@tanstack/react-query';
import { fetchCurrentUser } from '@wundo/client/auth.ts';
import { fetchOneOnOneColleagues, type OneOnOneColleagueItem } from '@/lib/colleagues.ts';
import { sortColleagues, type TeamSort } from './sortColleagues.ts';
import { inTeamFilter, type TeamFilter } from './teamFilter.ts';

/** The list under the Team page's toolbar. */
export type TeamList =
  | { kind: 'loading' }
  | { kind: 'failed' }
  | { kind: 'empty' }
  | { kind: 'cards'; colleagues: OneOnOneColleagueItem[] };

/** The Team page as a whole: the overview needs colleagues, or the hidden ones asked for. */
export type TeamContent =
  | { kind: 'loading' }
  | { kind: 'failed' }
  | { kind: 'empty' }
  | { kind: 'overview'; list: TeamList };

export interface TeamListView {
  filter: TeamFilter;
  search: string;
  sort: TeamSort;
  now: Date;
}

/**
 * The colleagues on the Team page, narrowed and ordered as the toolbar asks.
 * That happens here rather than on the server: the whole list is already loaded
 * and fits in a hand. Hidden colleagues are a list of their own, read on demand.
 */
export function useTeamColleagues(view: TeamListView) {
  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: fetchCurrentUser,
  });

  /* The summary and the counts always describe the page itself, never the hidden ones. */
  const {
    data: colleagues = [],
    isPending,
    isError,
  } = useQuery({
    queryKey: ['oneOnOneColleagues', user?.id, 'active'],
    queryFn: () => fetchOneOnOneColleagues(user!.id, { hidden: false }),
    enabled: Boolean(user?.id),
  });
  const hiddenList = useQuery({
    queryKey: ['oneOnOneColleagues', user?.id, 'hidden'],
    queryFn: () => fetchOneOnOneColleagues(user!.id, { hidden: true }),
    enabled: Boolean(user?.id) && view.filter === 'hidden',
  });

  const showingHidden = view.filter === 'hidden';
  const list = teamList(
    showingHidden ? hiddenList : null,
    shownColleagues(showingHidden ? (hiddenList.data ?? []) : colleagues, view),
  );

  return {
    colleagues,
    content: teamContent({
      isPending,
      isError,
      empty: colleagues.length === 0 && !showingHidden,
      list,
    }),
  };
}

function teamContent(page: {
  isPending: boolean;
  isError: boolean;
  empty: boolean;
  list: TeamList;
}): TeamContent {
  if (page.isPending) return { kind: 'loading' };
  if (page.isError) return { kind: 'failed' };
  if (page.empty) return { kind: 'empty' };
  return { kind: 'overview', list: page.list };
}

/* The hidden list loads only once asked for, so it has a loading line of its own. */
function teamList(
  hiddenList: { isPending: boolean; isError: boolean } | null,
  shown: OneOnOneColleagueItem[],
): TeamList {
  if (hiddenList?.isPending) return { kind: 'loading' };
  if (hiddenList?.isError) return { kind: 'failed' };
  if (shown.length === 0) return { kind: 'empty' };
  return { kind: 'cards', colleagues: shown };
}

function shownColleagues(
  candidates: OneOnOneColleagueItem[],
  { filter, search, sort, now }: TeamListView,
): OneOnOneColleagueItem[] {
  const wanted = search.trim().toLowerCase();
  const matches = (colleague: OneOnOneColleagueItem) =>
    wanted === '' ||
    colleague.label.toLowerCase().includes(wanted) ||
    colleague.emails.some((email) => email.toLowerCase().includes(wanted));

  return sortColleagues(
    candidates.filter(
      (colleague) => inTeamFilter(colleague, filter, now) && matches(colleague),
    ),
    sort,
  );
}
